import { useState } from "react";

import { Callout } from "@blueprintjs/core";

import {
  couldManageAttendees,
  isPendingBookingRequest,
} from "@/components/helpers/meeting/is-pending-booking-request";
import { Box } from "@/components/layout/flexbox";
import { InvitationsScheduledBanner } from "@/components/pieces/coaching/meetings/MeetingAttendees/components/AttendeeManageSection/InvitationsScheduledBanner";
import { InviteAttendeesDialog } from "@/components/pieces/coaching/meetings/MeetingAttendees/components/AttendeeManageSection/InviteAttendeesDialog";
import { ManageAttendeesDialog } from "@/components/pieces/coaching/meetings/MeetingAttendees/components/AttendeeManageSection/ManageAttendeesDialog";
import { Attendee, FullMeeting, UpcomingMeeting } from "@/types/meetings";
import { WorkspaceScheduleJob } from "@/types/workspaces";

type Meeting = FullMeeting | UpcomingMeeting;

interface AttendeeManageSectionProps {
  meeting: Meeting;
  attendees: Attendee[];
  onUpdateAttendee: () => void;
  scheduleJob?: WorkspaceScheduleJob | null;
}

export function AttendeeManageSection({
  meeting,
  attendees,
  onUpdateAttendee,
  scheduleJob,
}: AttendeeManageSectionProps) {
  const [isOpenInvitationDialog, setIsOpenInvitationDialog] = useState(false);
  const isPendingOrder = isPendingBookingRequest(meeting);
  const allowEditAttendees = couldManageAttendees(meeting);

  const [selectedScheduleJob, setSelectedScheduleJob] =
    useState<WorkspaceScheduleJob>();

  const [isOpenManageAttendeesDialog, setIsOpenManageAttendeesDialog] =
    useState(false);

  return (
    <>
      {isPendingOrder ? (
        <Box mt={2}>
          <Callout intent="primary" icon={null}>
            You will be able to add attendees once the coach accepts the
            booking.
          </Callout>
        </Box>
      ) : (
        <Box pt={2}>
          <a
            onClick={(event) => {
              event.preventDefault();
              onUpdateAttendee();
              setIsOpenManageAttendeesDialog(true);
            }}
          >
            {allowEditAttendees ? "Manage Attendees" : "See All Attendees"}
          </a>
        </Box>
      )}
      {allowEditAttendees && (
        <Box mt={3}>
          <InvitationsScheduledBanner
            onOpenInvitationDetail={(job) => {
              if (job) {
                onUpdateAttendee();
              }
              setSelectedScheduleJob(job);
              setIsOpenInvitationDialog(true);
            }}
            scheduleJob={scheduleJob}
          />
        </Box>
      )}
      <InviteAttendeesDialog
        open={isOpenInvitationDialog}
        afterAddOrUpdateSuccess={() => {
          setIsOpenInvitationDialog(false);
          onUpdateAttendee();
        }}
        onClose={() => {
          setIsOpenInvitationDialog(false);
          setSelectedScheduleJob(undefined);
        }}
        meeting={meeting}
        attendees={attendees}
        scheduleJob={selectedScheduleJob}
        hideSchedule={!!scheduleJob && !selectedScheduleJob}
      />
      <ManageAttendeesDialog
        open={isOpenManageAttendeesDialog}
        title={allowEditAttendees ? "Manage Attendees" : "All Attendees"}
        onAddAttendees={(job) => {
          if (job) onUpdateAttendee();

          setSelectedScheduleJob(job);
          setIsOpenManageAttendeesDialog(false);
          setIsOpenInvitationDialog(true);
        }}
        onClose={() => {
          setIsOpenManageAttendeesDialog(false);
          setSelectedScheduleJob(undefined);
        }}
        meeting={meeting}
        attendees={attendees}
        onUpdateAttendee={onUpdateAttendee}
        scheduleJob={scheduleJob}
      />
    </>
  );
}
